import React, { forwardRef, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicatorIcon } from '@panwds/icons';
import { classNames } from '@panwds/tailwindcss';
import { TruncateText } from '../TruncateText/TruncateText.esm.js';
import { useTabsContext } from './TabsContext.esm.js';

function Tab({ active, size = 'md', disabled = false, draggable = false, dataTestId, leftIcon, rightIcon, id, ...props }) {
    const buttonRef = useRef(null);
    const { isDragging, attributes, listeners, setNodeRef, transform } = useSortable({
        id: String(id)
    });
    const intl = useIntl();
    const style = {
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 999 : 'auto'
    };
    const { orientation } = useTabsContext();
    const isVertical = orientation === 'vertical';
    const defaultClasses = classNames('tw-flex tw-items-center tw-border-solid tw-border-r-0 tw-border-t-0 tw-bg-white dark:tw-bg-gray-1050 tw-box-border', !isVertical ? 'tw-border-l-0' : 'tw-border-b-0');
    const sizeClasses = classNames('tw-text-sm tw-leading-6 tw-h-8', size === 'sm' && 'tw-text-xs tw-h-6', size === 'lg' && 'text-base tw-h-10');
    const hoverClasses = classNames(!disabled &&
        'hover:tw-bg-blue-100 dark:hover:tw-bg-gray-800 active:tw-bg-blue-200 dark:active:tw-bg-gray-700 focus-visible:tw-ring focus-visible:tw-ring-inset tw-box-border');
    const buttonProps = {
        ...props,
        id: `button-${id}`,
        size,
        disabled,
        tabIndex: props.hasFocus ? 0 : -1,
        className: classNames(!draggable && 'tw-pl-4', !disabled && 'tw-cursor-pointer tw-text-default dark:tw-text-gray-100', disabled && 'dark:tw-text-gray-600', 'tw-flex tw-items-center tw-font-semibold', sizeClasses)
    };
    const classes = classNames(defaultClasses, hoverClasses, disabled && 'tw-text-disabled', active
        ? isVertical
            ? 'tw-border-l-2 tw-border-blue-700 dark:tw-border-blue-400 tw-bg-blue-200 dark:tw-bg-[#333333] active:tw-border-l-blue-700'
            : 'tw-border-b-2 tw-border-blue-700 dark:tw-border-blue-400 active:tw-border-b-blue-700'
        : isVertical
            ? 'tw-border-0 tw-border-l tw-ml-px tw-border-transparent'
            : 'tw-border-b tw-mb-px tw-border-transparent');
    useEffect(() => {
        var _a;
        if (props.hasFocus)
            (_a = buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [props.hasFocus, buttonRef]);
    return (React.createElement("div", { id: id, "data-testid": dataTestId || `tab-${id}`, style: style, ref: setNodeRef, className: classNames('tw-group', classes), role: "tab" },
        React.createElement(Button, { ...buttonProps, ref: buttonRef },
            draggable && (React.createElement("span", { className: classNames('tw-flex tw-justify-center tw-items-center tw-w-6') },
                React.createElement("span", { "aria-label": intl.formatMessage({
                        defaultMessage: 'Drag to reorder',
                        id: '2M7oVN',
                        description: 'Move Tab'
                    }), ...attributes, ...listeners, className: classNames('tw-flex tw-items-center tw-border-none tw-bg-transparent tw-opacity-0 dark:tw-text-dark-bg', !disabled && 'group-hover:tw-opacity-100') },
                    React.createElement(DragIndicatorIcon, { className: classNames('tw-text-gray-600 dark:tw-text-gray-400', !disabled && 'tw-cursor-move'), size: "xs" })))),
            leftIcon && (React.createElement("span", { className: "tw-flex tw-items-center tw-mr-2 tw-pointer-events-none tw-text-gray-600 dark:tw-text-gray-400" }, leftIcon)),
            React.createElement(TruncateText, { addClassName: "tw-pointer-events-none" }, buttonProps.children),
            rightIcon && (React.createElement("span", { className: "tw-flex tw-items-center tw-ml-2 tw-pointer-events-none tw-text-gray-600 dark:tw-text-gray-400" }, rightIcon)),
            React.createElement("span", { className: classNames('tw-pointer-events-none', draggable || isVertical ? 'tw-w-6' : 'tw-w-4') }))));
}
const Button = forwardRef(({ id, active = false, children, dataMetrics, dataTestId, disabled = false, onClick, onKeyDown, tabIndex, className }, ref) => {
    const classes = classNames('tw-box-border tw-border-0 tw-border-r-0 tw-border-l-0 tw-border-t-0 tw-bg-transparent tw-flex tw-items-center tw-p-0', className);
    return (React.createElement("button", { "data-metrics": dataMetrics, "data-testid": dataTestId || id, id: id, className: classes, "aria-selected": active, disabled: disabled, onClick: onClick, onKeyDown: onKeyDown, ref: ref, tabIndex: tabIndex, type: "button", style: { maxWidth: '16rem' } }, children));
});
Button.displayName = 'Button';

export { Tab };
//# sourceMappingURL=Tab.esm.js.map
